import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView } from 'react-native-gesture-handler';

import { useNavigation } from '@react-navigation/native';

import { Button, ButtonProps } from '@/components/ButtonComponent';
import { Text } from '@/components/TextComponent';
import { ds } from '@/components/lib/design.system';
import { useAlerts } from '@/feature/alerts/hooks/use-alerts';
import BetrAnalytics from '@/feature/analytics/analytics';
import { AnalyticsEvent } from '@/feature/analytics/constants';
import { useShowTncSheet } from '@/feature/terms-and-conditions/hooks/use-show-tnc-sheet';
import { useIsGameplayOfflineForActiveProductWallet } from '@/hooks/use-active-product';
import { productNames } from '@/hooks/use-active-product-name';
import { useActiveWallet, useActiveWalletStore, useSwitchActiveWallet } from '@/hooks/use-active-wallet';
import { useAgeCohortValidation } from '@/hooks/use-age-cohort-validation';
import { isUserBelowAgeLimitToPlaySelector, useAuthUserConfig } from '@/hooks/use-auth-user-config';
import { useAvailableProducts } from '@/hooks/use-available-products';
import { Product, getProductType, useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { useSocialWalletTranslation } from '@/hooks/use-social-wallet-translation';

import { styles } from './styles';

const BeforeYouDeposit = ({ text }: { text: string }) => (
    <ScrollView style={styles.beforeYouDepositSheet}>
        <Text color={ds.colorToken.text.secondary} variant="bodyMedium">
            {text}
        </Text>
    </ScrollView>
);

type DepositButtonProps = {
    customLabel?: string;
    buttonHierarchy?: ButtonProps['hierarchy'];
};

const BaseDepositButton = ({ onPress, buttonHierarchy, customLabel }: { onPress: () => void } & DepositButtonProps) => {
    const { t } = useTranslation(['transactions']);
    const { activeWalletProductName } = useActiveWallet();
    const { showDepositAgeRestrictionNotice } = useAgeCohortValidation();
    const { data: isBelowAgeLimitToPlay } = useAuthUserConfig({ select: isUserBelowAgeLimitToPlaySelector });
    const showTncSheet = useShowTncSheet();

    const handlePress = () => {
        if (isBelowAgeLimitToPlay) {
            showDepositAgeRestrictionNotice();
            return;
        }

        BetrAnalytics.trackProductEvent(AnalyticsEvent.WALLET_DEPOSIT);
        showTncSheet(onPress);
    };

    return (
        <Button
            hierarchy={buttonHierarchy || 'primary'}
            onPress={handlePress}
            label={customLabel || t('deposit_to_product', { product: activeWalletProductName })}
        />
    );
};

const DepositButtonWithWarningSheet = ({
    text,
    buttonHierarchy,
    customLabel,
}: { text: string } & DepositButtonProps) => {
    const { t } = useTranslation(['common', 'transactions']);
    const tSocialWallet = useSocialWalletTranslation(['kyc', 'transactions']);
    const navigation = useNavigation();

    const { activeWalletProductName } = useActiveWallet();
    const switchActiveWallet = useSwitchActiveWallet();

    const { showInfoSheet } = useAlerts();
    const alternativeGameplayOnlineProduct = useAlternativeGameplayOnlineProduct();

    const goToDepositScreen = () => navigation.navigate('DepositStack', { screen: 'DepositScreen', params: {} });
    const secondaryLabel = alternativeGameplayOnlineProduct
        ? t('go_to_product', { product: productNames[alternativeGameplayOnlineProduct] })
        : t('cancel');

    const handlePress = () => {
        showInfoSheet({
            title: tSocialWallet('before_you_deposit'),
            description: <BeforeYouDeposit text={text} />,
            buttonLabel: tSocialWallet('transactions:deposit_to_product', { product: activeWalletProductName }),
            secondaryLabel,
            handlePress: () => goToDepositScreen(),
            handleSecondaryPress: () => {
                if (alternativeGameplayOnlineProduct) {
                    switchActiveWallet(alternativeGameplayOnlineProduct);
                    goToDepositScreen();
                }
            },
        });
    };

    return <BaseDepositButton onPress={handlePress} buttonHierarchy={buttonHierarchy} customLabel={customLabel} />;
};

const DepositButtonWithNavigation = (props: DepositButtonProps) => {
    const navigation = useNavigation();

    const handlePress = () => {
        navigation.navigate('DepositStack', { screen: 'DepositScreen', params: {} });
    };
    return <BaseDepositButton onPress={handlePress} {...props} />;
};

const UnavailableDepositButton = (props: DepositButtonProps) => {
    const { t } = useTranslation('common');
    const tSocialWallet = useSocialWalletTranslation('wallet');
    const { showInfoSheet } = useAlerts();

    const handlePress = () => {
        showInfoSheet({
            title: tSocialWallet('deposits_unavailable'),
            description: tSocialWallet('unavailable_deposit'),
            buttonLabel: t('close'),
        });
    };
    return <BaseDepositButton onPress={handlePress} {...props} />;
};

/**
 * DepositButton Component at Account Details screen.
 *
 * - Displays the appropriate deposit button based on the conditions.
 */
export const DepositButton = ({ customLabel, buttonHierarchy }: DepositButtonProps) => {
    const { activeWalletProduct } = useActiveWallet();

    const settings = useJurisdictionStore(state => state.jurisdictionSettings?.productConfig?.settings);
    const beforeYouDepositInfo = useJurisdictionStore(
        state => state.jurisdictionSettings?.globalSettings?.keyValuePairs?.BeforeYouDepositInfo
    );
    const beforeYouDepositWarningText = beforeYouDepositInfo?.[activeWalletProduct] ?? '';

    const gameplayOfflineForActiveWalletProduct = useIsGameplayOfflineForActiveProductWallet();

    const shouldShowBeforeYouDepositWarning = useMemo(
        () => beforeYouDepositWarningText && gameplayOfflineForActiveWalletProduct,
        [beforeYouDepositWarningText, gameplayOfflineForActiveWalletProduct]
    );

    if (settings?.deposit_enabled) {
        if (shouldShowBeforeYouDepositWarning) {
            return (
                <DepositButtonWithWarningSheet
                    text={beforeYouDepositWarningText}
                    buttonHierarchy={buttonHierarchy}
                    customLabel={customLabel}
                />
            );
        }
        if (!shouldShowBeforeYouDepositWarning) {
            return <DepositButtonWithNavigation buttonHierarchy={buttonHierarchy} customLabel={customLabel} />;
        }
    }

    return <UnavailableDepositButton buttonHierarchy={buttonHierarchy} customLabel={customLabel} />;
};

/**
 * Hook returning the alternative Product that has gameplay online, where the user can deposit.
 * If there is no alternative Product in this state, or there are multiple alternative product,
 * this returns undefined as we don't know what Product we should recommend the user to deposit to.
 */
const useAlternativeGameplayOnlineProduct = (): Product | undefined => {
    const activeWalletProduct = useActiveWalletStore(state => state.activeWalletProduct);
    const usState = useJurisdictionStore(state => state.jurisdiction);
    const { availableProducts } = useAvailableProducts();
    const productsConfigs = useJurisdictionStore(state => state.jurisdictionSettings?.products);
    const alternativeProducts = availableProducts.filter(it => it !== activeWalletProduct) ?? [];
    //Only suggest alternative product if there is one. When there are multiple alternative products
    //we can't decide which to suggest for user to deposit
    if (alternativeProducts.length === 1) {
        const otherProduct = alternativeProducts[0];
        const otherProductConfig = productsConfigs?.find(it => getProductType(it.uid) === otherProduct);
        if (!otherProductConfig?.gameplayOfflineStates.includes(usState ?? '')) {
            return getProductType(otherProduct);
        }
    }

    return undefined;
};
